/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { convertDateInputTo8AMUTC } from '../UtilityFunctions';

type UseLoanSubsidyOptions = {
  /** When set, fetches subsidies for this loan instead of LoanContext (e.g. Payments row loan). */
  loanNumber?: string;
  /** When false, skips the subsidy list query. Defaults to true. */
  enabled?: boolean;
};

type UseLoanSubsidy = {
  loanSubsidy: UnknownSubsidyResp;
  loadingLoanSubsidy: boolean;
  createLoanSubsidy: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  updateLoanSubsidy: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  reloadLoanSubsidy: () => void;
};

const useLoanSubsidy = (pathNameOrCnt = '/list', options: UseLoanSubsidyOptions = {}): UseLoanSubsidy => {
  const { get } = useAbortController();
  const { loanNumber } = useLoanContext();
  const { token } = useAuthContext();
  const queryLoanNumber = options.loanNumber ?? loanNumber;
  const queryEnabled = (options.enabled ?? true) && Boolean(token) && Boolean(queryLoanNumber);
  const queryClient = useQueryClient(); // Use the query client directly
  const path = useMemo(() => {
    const cnt = typeof pathNameOrCnt === 'string' ? new String(pathNameOrCnt) : new String('list');

    return `/${cnt}`;
  }, [pathNameOrCnt]);

  const queryKey = useMemo(() => ['loanSubsidies', queryLoanNumber, path, token], [queryLoanNumber, path, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    enabled: queryEnabled,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        if (!path || !token) return { data: null };
        const request = {
          url: `live/loan/${queryLoanNumber}/subsidy${path}`,
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanSubsidy.response: `, response);
        return { data: response };
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error(`useLoanSubsidy.caught: ${error.message}`);
        }
        // Simulated fetch call for testing, TODO: Remove before public release.
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanSubsidy = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  // TODO: this function is a skeleton and needs to be updated to function properly
  const createLoanSubsidy = useCallback(
    async (fields, setErrors) => {
      if (!token) throw new Error('!token');
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      fields.startDate = convertDateInputTo8AMUTC(fields.startDate).getTime() / 1000;
      fields.endDate = convertDateInputTo8AMUTC(fields.endDate).getTime() / 1000;
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan/${loanNumber}/subsidy/add`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useLoanSubsidy.response: `, response);
      reloadLoanSubsidy();
      return response;
    },
    [loanNumber, get, reloadLoanSubsidy, token],
  );

  const updateLoanSubsidy = useCallback(
    async (fields, setErrors) => {
      if (!token) throw new Error('!token');
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      // Reset the date fields to be dates. These are date only fields, divide by 1000.
      fields.startDate = convertDateInputTo8AMUTC(fields.startDate).getTime() / 1000;
      fields.endDate = convertDateInputTo8AMUTC(fields.endDate).getTime() / 1000;
      fields.terminationDate = convertDateInputTo8AMUTC(fields.terminationDate).getTime() / 1000;
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));

      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan/${loanNumber}/subsidy/${fields.cnt}`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useLoanSubsidy.response: `, response);
      reloadLoanSubsidy();
      return response;
    },
    [loanNumber, path, get, reloadLoanSubsidy, token],
  );

  return {
    loanSubsidy: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loadingLoanSubsidy: isLoading,
    createLoanSubsidy,
    updateLoanSubsidy,
    reloadLoanSubsidy,
  };
};

export default useLoanSubsidy;
